import {
	BankTransferScope,
	useGetFullOrderPaymentPreviewQuery,
	useGetProductPaymentPreviewQuery,
	useGetSamplePaymentPreviewQuery,
	useRecordOrderBankTransferMutation,
} from '@codezo/graphql';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CurrencyText } from '../../../components/CurrencyText';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { DatePicker } from '../../../components/ui/date-picker';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Radio } from '../../../components/ui/radio';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../lib/utils';

// The invoice route books in DKK, as when a client picks "pay by invoice".
const CURRENCY = 'DKK';

const SCOPE_LABEL: Record<BankTransferScope, { title: string; detail: string }> = {
	[BankTransferScope.Samples]: {
		title: 'Samples',
		detail: 'Every sample still waiting for payment.',
	},
	[BankTransferScope.Products]: {
		title: 'Products',
		detail: 'Production for the products you pick.',
	},
	[BankTransferScope.FullOrder]: {
		title: 'Whole order',
		detail: 'Samples and production on one invoice.',
	},
};

/** YYYY-MM-DD in local time — the day the admin picked, not its UTC shadow. */
const toDay = (date: Date) =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * Records money a client wired over without paying through the platform.
 *
 * The scopes are the client's own pay routes, offered only while there is
 * something on them — the previews return exactly what is payable now, so the
 * dialog never offers what the server would refuse. Amounts are the previews'
 * gross figures: what the invoice that gets booked will say.
 */
export function RecordBankTransferModal({
	open,
	onOpenChange,
	orderId,
	onRecorded,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orderId: string;
	onRecorded: () => void;
}) {
	const [scope, setScope] = useState<BankTransferScope | null>(null);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [receivedOn, setReceivedOn] = useState<Date | undefined>(new Date());
	const [reference, setReference] = useState('');
	const [note, setNote] = useState('');
	const [sendInvoice, setSendInvoice] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [confirmOpen, setConfirmOpen] = useState(false);

	const [recordTransfer, { loading: recording }] = useRecordOrderBankTransferMutation();

	// Unscoped: everything payable right now, which is what decides the options.
	const { data: productData, loading: productLoading } = useGetProductPaymentPreviewQuery({
		variables: { orderId, currency: CURRENCY },
		skip: !open,
		fetchPolicy: 'no-cache',
	});
	const { data: sampleData, loading: sampleLoading } = useGetSamplePaymentPreviewQuery({
		variables: { orderId, currency: CURRENCY },
		skip: !open,
		fetchPolicy: 'no-cache',
	});
	const { data: fullData, loading: fullLoading } = useGetFullOrderPaymentPreviewQuery({
		variables: { orderId, currency: CURRENCY },
		skip: !open,
		fetchPolicy: 'no-cache',
	});
	// Priced for the products actually ticked, so the amount follows the selection.
	const { data: selectionData, loading: selectionLoading } = useGetProductPaymentPreviewQuery({
		variables: { orderId, currency: CURRENCY, orderItemIds: selectedItemIds },
		skip: !open || scope !== BankTransferScope.Products || selectedItemIds.length === 0,
		fetchPolicy: 'no-cache',
	});

	const payableProducts = productData?.getProductPaymentPreview.items ?? [];
	const payableSamples = sampleData?.getSamplePaymentPreview.items ?? [];
	const loadingOptions = productLoading || sampleLoading || fullLoading;

	const available = useMemo(() => {
		const scopes: BankTransferScope[] = [];
		if (payableSamples.length > 0) scopes.push(BankTransferScope.Samples);
		if (payableProducts.length > 0) scopes.push(BankTransferScope.Products);
		// Only distinct from "Products" while there are samples to carry with it.
		if (
			payableSamples.length > 0 &&
			(fullData?.getFullOrderPaymentPreview.items.length ?? 0) > 0
		) {
			scopes.push(BankTransferScope.FullOrder);
		}
		return scopes;
	}, [payableSamples.length, payableProducts.length, fullData]);

	useEffect(() => {
		if (!open) return;
		setReceivedOn(new Date());
		setReference('');
		setNote('');
		setSendInvoice(false);
		setErrors({});
	}, [open]);

	// Default to the first open route, and every payable product ticked.
	useEffect(() => {
		if (!open || loadingOptions) return;
		setScope((current) =>
			current && available.includes(current) ? current : (available[0] ?? null)
		);
		setSelectedItemIds(payableProducts.map((item) => item.orderItemId));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, loadingOptions, available]);

	const amount =
		scope === BankTransferScope.Samples
			? sampleData?.getSamplePaymentPreview.grossAmount
			: scope === BankTransferScope.FullOrder
				? fullData?.getFullOrderPaymentPreview.grossAmount
				: selectedItemIds.length > 0
					? selectionData?.getProductPaymentPreview.grossAmount
					: 0;
	const amountLoading = scope === BankTransferScope.Products && selectionLoading;

	const toggleItem = (id: string, checked: boolean) =>
		setSelectedItemIds((ids) => (checked ? [...ids, id] : ids.filter((x) => x !== id)));

	const validate = () => {
		const e: Record<string, string> = {};
		if (!scope) e.scope = 'Pick what the transfer paid for.';
		if (scope === BankTransferScope.Products && selectedItemIds.length === 0) {
			e.scope = 'Pick at least one product.';
		}
		if (!receivedOn) e.receivedOn = 'When did the money land?';
		if (!reference.trim()) e.reference = 'Add the reference from the bank statement.';
		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const handleConfirm = async () => {
		if (!scope || !receivedOn) return;
		try {
			const { data } = await recordTransfer({
				variables: {
					input: {
						orderId,
						scope,
						orderItemIds: scope === BankTransferScope.Products ? selectedItemIds : null,
						reference: reference.trim(),
						receivedOn: toDay(receivedOn),
						note: note.trim() || null,
						sendInvoice,
					},
				},
			});
			const result = data?.recordOrderBankTransfer;
			setConfirmOpen(false);
			if (!result?.success) {
				toast.error(result?.message || 'Failed to record the bank transfer');
				// Booked but not settled still changed the order — the invoice now
				// shows under Transactions, waiting for "Mark invoice paid".
				if (result?.paymentTransactionId) {
					onOpenChange(false);
					onRecorded();
				}
				return;
			}
			toast.success(result.message || 'Bank transfer recorded');
			onOpenChange(false);
			onRecorded();
		} catch {
			toast.error('Failed to record the bank transfer');
		}
	};

	const scopeSummary =
		scope === BankTransferScope.Products
			? `${selectedItemIds.length} product${selectedItemIds.length === 1 ? '' : 's'}`
			: scope === BankTransferScope.Samples
				? `${payableSamples.length} sample${payableSamples.length === 1 ? '' : 's'}`
				: 'the whole order';

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Record bank transfer</DialogTitle>
					</DialogHeader>
					<DialogForm onSubmit={() => validate() && setConfirmOpen(true)}>
						<DialogBody className="flex flex-col gap-5">
							<p className="text-sm text-text-3">
								For money the client sent straight to our account. An invoice is
								booked in e-conomic and marked paid right away, so the books and the
								order both show it.
							</p>

							{loadingOptions ? (
								<div className="h-24 animate-pulse rounded-lg bg-surface-fill" />
							) : available.length === 0 ? (
								<p className="rounded-[var(--r-2)] border border-line-1 p-3 text-sm text-text-3">
									Nothing on this order is waiting for payment. Anything already
									invoiced is settled with “Mark invoice paid” under Transactions.
								</p>
							) : (
								<Field label="What did it pay for?" error={errors.scope}>
									<div className="flex flex-col gap-2">
										{available.map((option) => (
											<button
												key={option}
												type="button"
												onClick={() => setScope(option)}
												className={cn(
													'flex cursor-pointer items-start gap-3 rounded-[var(--r-2)] border p-3 text-left transition-colors',
													scope === option
														? 'border-accent bg-surface-fill'
														: 'border-line-1 hover:bg-surface-fill'
												)}
											>
												<Radio
													checked={scope === option}
													className="mt-0.5"
												/>
												<span className="flex flex-col">
													<span className="text-sm text-text-1">
														{SCOPE_LABEL[option].title}
													</span>
													<span className="text-xs text-text-3">
														{SCOPE_LABEL[option].detail}
													</span>
												</span>
											</button>
										))}
									</div>
								</Field>
							)}

							{scope === BankTransferScope.Products && (
								<div className="flex flex-col gap-2">
									{payableProducts.map((item) => (
										<label
											key={item.orderItemId}
											className="flex cursor-pointer items-center justify-between gap-3 text-sm"
										>
											<span className="flex items-center gap-2.5">
												<Checkbox
													checked={selectedItemIds.includes(
														item.orderItemId
													)}
													onCheckedChange={(checked) =>
														toggleItem(item.orderItemId, checked)
													}
												/>
												<span className="text-text-1">
													{item.description}
												</span>
											</span>
											<CurrencyText
												amount={item.lineNetAmount}
												currency={CURRENCY}
												className="text-text-3"
											/>
										</label>
									))}
								</div>
							)}

							{scope && (
								<div className="flex items-baseline justify-between rounded-[var(--r-2)] bg-surface-fill px-3 py-2.5">
									<span className="text-sm text-text-3">
										Invoice total incl. VAT
									</span>
									<span className="font-mono text-text-1">
										{amountLoading ? (
											'…'
										) : (
											<CurrencyText amount={amount} currency={CURRENCY} />
										)}
									</span>
								</div>
							)}

							<div className="grid gap-4 sm:grid-cols-2">
								<Field label="Received on" required error={errors.receivedOn}>
									<DatePicker value={receivedOn} onChange={setReceivedOn} />
								</Field>
								<Field label="Bank reference" required error={errors.reference}>
									<Input
										value={reference}
										onChange={(e) => setReference(e.target.value)}
										placeholder="Text on the bank statement"
									/>
								</Field>
							</div>

							<Field label="Note">
								<Textarea
									value={note}
									onChange={(e) => setNote(e.target.value)}
									placeholder="Optional — anything the next person should know"
									rows={2}
								/>
							</Field>

							<label className="flex cursor-pointer items-center justify-between gap-3">
								<span className="flex flex-col">
									<span className="text-sm text-text-1">
										Email the invoice to the client
									</span>
									<span className="text-xs text-text-3">
										Off by default — they’ve already paid. It’s for our books.
									</span>
								</span>
								<Switch checked={sendInvoice} onCheckedChange={setSendInvoice} />
							</label>
						</DialogBody>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={!scope || loadingOptions || recording}>
								Record transfer
							</Button>
						</DialogFooter>
					</DialogForm>
				</DialogContent>
			</Dialog>

			<ConfirmDialog
				open={confirmOpen}
				onOpenChange={setConfirmOpen}
				variant="default"
				title="Record bank transfer"
				confirmLabel="Book and mark paid"
				loading={recording}
				onConfirm={handleConfirm}
				description={
					<>
						This books an invoice for {scopeSummary}
						{amount != null && (
							<>
								{' '}
								(<CurrencyText amount={amount} currency={CURRENCY} />)
							</>
						)}{' '}
						in e-conomic and marks it paid with reference “{reference.trim()}”.{' '}
						{sendInvoice
							? 'The invoice is emailed to the client, and they get a payment receipt.'
							: 'The invoice isn’t emailed; the client gets a payment receipt.'}{' '}
						To undo it, the invoice has to be credited in e-conomic.
					</>
				}
			/>
		</>
	);
}
